/* eslint-disable max-len */
/* for custom values with fixed cusval id */
import React, { useEffect, useState } from 'react';

import _ from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { Box, Grid } from '@mui/material';

import OhsCusvalFieldFactory from './OhsCusvalFieldFactory';
import { InvalidCusvalTypes, OhsCusval, OhsCusvalType } from './OhsCusvalModel';
import { setCusvalValueToForm, returnInvalidCusvals } from './OhsCusvalServices';
import { OhsFormLayoutType, OhsFormSettings } from '../services/OhsFormModels';
import { RadioOptions } from '../fields/OhsRadioField';

interface Props {
  id: string;
  ohsFormSettings: OhsFormSettings;
  gridOptions?: any;

  cusvalsConfig: OhsCusval[] | null;
  isInitCusvalConfigFetched: boolean;

  // for injury system field
  templateId?: string;
  recordId?: string;
  taskId?: string;
  maxCount?: number;
}

const useStyles = makeStyles()(() => ({
  invalidContainer: {
    position: 'relative',
    width: '100%',
  },
  invalidOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#9f3a38',
    opacity: 0.05,
  },
  invalidMessage: {
    color: '#9f3a38',
    fontSize: '12px',
  },
  container: {
    width: '100%',
  },
}));

export default function OhsFixedCategoryCusvalFieldsSectionContent(props: Props) {
  const { classes } = useStyles();

  const [initialCusval, setInitialCusval] = useState<OhsCusval[] | null>(null); // prop cusvals before being formatted to form obj
  const [isFormatted, setIsFormatted] = useState<boolean>(false); // if prop cusvals is formatted to form obj
  const [invalidCusvals, setInvalidCusval] = useState<OhsCusval[]>([]); // when cusvals are deleted from a category but still has values
  const [invalidIsInitialized, setInvalidIsInitialized] = useState<boolean>(false);

  const ohsFormSettings = new OhsFormSettings({}, props.ohsFormSettings.layout);
  const propValues = props.ohsFormSettings.useFormMethods.getValues();
  const cusvalPropValues = propValues[props.id] || [];
  const formValues = ohsFormSettings.useFormMethods.watch(props.id);

  const formSubmit = () => {};

  props.ohsFormSettings.useFormMethods.register(props.id, {
    validate: {
      noErrors: async () => {
        const withError = await ohsFormSettings.useFormMethods
          .handleSubmit(formSubmit)()
          .then(() => {
            const error = _.get(ohsFormSettings.useFormMethods.formState.errors, props.id);
            if (!_.isUndefined(error) && !_.isEmpty(error) && !_.isNil(error)) {
              return true;
            }
            return false;
          });
        return !withError;
      },
    },
  });

  const returnCusval = (item: OhsCusval, index: number, isInvalid?: boolean) => {
    let additionalProps = {};
    if (isInvalid) {
      if (item.type === OhsCusvalType.Currency) {
        const options: string[] = [item.value?.code || ''];
        additionalProps = { ...additionalProps, options };
      }
      if (item.type === OhsCusvalType.Radio || item.type === OhsCusvalType.Checkbox) {
        let options: RadioOptions = [];
        (item.value || []).forEach((val: string) => {
          options = [...options, { name: val, value: val }];
        });
        additionalProps = { ...additionalProps, options };
      }
      if (item.type === OhsCusvalType.Select || item.type === OhsCusvalType.MutipleSelect) {
        let options: Array<any> = [];
        (item.value || []).forEach((val: string) => {
          options = [...options, { name: val[0] }];
        });
        additionalProps = { ...additionalProps, options };
      }
    }

    return (
      <Grid
        key={index}
        item
        xs={12}
        sm={
          ohsFormSettings.layout === OhsFormLayoutType.AngularModalForm &&
          (item.type === OhsCusvalType.Text ||
            item.type === OhsCusvalType.Color ||
            item.type === OhsCusvalType.Date ||
            item.type === OhsCusvalType.Time ||
            item.type === OhsCusvalType.Url ||
            item.type === OhsCusvalType.Email ||
            item.type === OhsCusvalType.Number)
            ? 4
            : false
        }
      >
        <OhsCusvalFieldFactory
          key={index}
          sectionId={props.id}
          cusval={{
            ...item,
            ...additionalProps,
          }}
          ohsFormSettings={ohsFormSettings}
          templateId={props.templateId}
          recordId={props.recordId}
          taskId={props.taskId}
          maxCount={props.maxCount}
        />
      </Grid>
    );
  };

  // display invalid cusvals
  const renderInvalidCusvals = (cusvalsToDisplay: OhsCusval[]) => {
    if (cusvalsToDisplay.length < 1) return '';
    const renderMessage = () => {
      return (
        <>
          <Box className={classes.invalidOverlay} />
          <Box pt={1} px={1} className={classes.invalidMessage}>
            <b>Note:</b> {cusvalsToDisplay.length > 1 ? 'These fields have' : 'This field has'} been
            removed, associated data will be deleted if you update/edit
          </Box>
        </>
      );
    };

    if (ohsFormSettings.layout === OhsFormLayoutType.AngularPageForm) {
      return (
        <Box py={1} className={classes.invalidContainer}>
          {(cusvalsToDisplay || []).map((item, index) => {
            const cusvalItem = {
              ...item,
              disabled: true,
              required: false,
            };
            return returnCusval(cusvalItem, index, true);
          })}
          {renderMessage()}
        </Box>
      );
    }
    return (
      <Box ml={3} mt={3} className={classes.invalidContainer}>
        {(cusvalsToDisplay || []).map((item, index) => {
          const cusvalItem = {
            ...item,
            disabled: true,
            required: false,
          };
          return returnCusval(cusvalItem, index, true);
        })}
        {renderMessage()}
      </Box>
    );
  };

  const displayInvalidCusvals = () => {
    return (
      invalidCusvals.length > 0 && (
        <Box
          className={classes.container}
          data-testid={`${props.id}-${InvalidCusvalTypes.InvalidCusval}`}
        >
          {renderInvalidCusvals(invalidCusvals)}
        </Box>
      )
    );
  };

  // handle invalid cusvals
  useEffect(() => {
    // fill invalid cusval state only once
    if (
      !invalidIsInitialized &&
      initialCusval &&
      props.cusvalsConfig &&
      props.isInitCusvalConfigFetched
    ) {
      const newInvalidCusvals = returnInvalidCusvals(initialCusval, props.cusvalsConfig);
      setInvalidCusval(newInvalidCusvals);
      setInvalidIsInitialized(true);
    }
  }, [initialCusval, props.cusvalsConfig, props.isInitCusvalConfigFetched]);

  // changes from local ohsFormSettings updates prop ohsFormSettings
  useEffect(() => {
    if (props.cusvalsConfig) {
      props.ohsFormSettings.useFormMethods.setValue(props.id, {
        ...formValues,
        config: props.cusvalsConfig,
      });
    }
  }, [JSON.stringify(formValues), props.cusvalsConfig]);

  // convert array cusval into object compatible to form fields and initialize local ohsFormSettings values once
  useEffect(() => {
    async function formatForm() {
      if (!isFormatted && !_.isNil(props.id)) {
        if (!_.isEmpty(cusvalPropValues) && Array.isArray(cusvalPropValues)) {
          setInitialCusval(cusvalPropValues);

          const formattedCusval = setCusvalValueToForm(cusvalPropValues);
          ohsFormSettings.useFormMethods.setValue(props.id, formattedCusval);
          setIsFormatted(true);
        }
      }
    }
    formatForm();
  }, [isFormatted, cusvalPropValues]);

  return (
    <Grid
      container
      spacing={
        ohsFormSettings.layout === OhsFormLayoutType.AngularModalForm ||
        ohsFormSettings.layout === OhsFormLayoutType.AngularModalUnderlinedForm
          ? 2
          : 0
      }
      {...props.gridOptions}
    >
      {displayInvalidCusvals()}
      <Box className={classes.container} data-testid={props.id}>
        {(props.cusvalsConfig || []).map((item, index) => returnCusval(item, index))}
      </Box>
    </Grid>
  );
}

OhsFixedCategoryCusvalFieldsSectionContent.defaultProps = {
  gridOptions: {},
  templateId: undefined,
  recordId: undefined,
  taskId: undefined,
  maxCount: undefined,
};
